import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {AccountBatchSearchCriteriaImpl} from '../../../sdk/component/search/bulk-account-search-criteria';
import {SearchService} from '../../../sdk/component/search/search.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {AccountBatchSearchCriteria} from 'web-angular-client';
import {SearchFilterBase} from '../../../sdk/component/search/search-filter-base';
import {FilterPersistenceService} from '../../../sdk/component/search/filter-persistence.service';
import {ActivatedRoute} from '@angular/router';
import {ROUTES} from '../../../app.routes';
import {formatLocalYMD, toUtcIsoAtLocalEndOfDay, toUtcIsoAtLocalStartOfDay} from '../../../sdk/util/searchUtil';

@Component({
    selector: 'app-bulk-payment-search',
    templateUrl: 'bulk-payment-search.component.html',
    styleUrls: ['bulk-payment-search.component.scss'],
    standalone: false
})
export class BulkPaymentSearchComponent extends SearchFilterBase implements OnInit, OnDestroy
{
    form: UntypedFormGroup;
    private _BATCH_NUMBER_INPUT_ID: string = 'batchNumber';
    private _BATCH_NAME_INPUT_ID: string = 'batchName';
    private _CREATED_LOWER_INPUT_ID: string = 'createdLower';
    private _CREATED_UPPER_INPUT_ID: string = 'createdUpper';
    private _TOTAL_REQUESTS_LOWER_INPUT_ID: string = 'totalRequestsLower';
    private _TOTAL_REQUESTS_UPPER_INPUT_ID: string = 'totalRequestsUpper';
    private BATCH_NUMBER_FILTER_ID = 'batchNumberFilter';
    private BATCH_NAME_FILTER_ID = 'nameFilter';
    private CREATED_FILTER_ID = 'createdFilter';
    private TOTAL_REQUESTS_FILTER_ID = 'totalRequestsFilter';
    private accountBatchSearchCriteria: AccountBatchSearchCriteria = new AccountBatchSearchCriteriaImpl();

    constructor(
        protected searchDataService: SearchService,
        protected filterPersistenceService: FilterPersistenceService,
        protected route: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        protected ngxLoader: NgxUiLoaderService)
    {
        super(searchDataService, ngxLoader, filterPersistenceService, route);
    }

    get activeTab(): string
    {
        return this._activeTab;
    }

    get batchNumber(): string
    {
        return this.form.get(this._BATCH_NUMBER_INPUT_ID).value;
    }

    get batchName(): string
    {
        return this.form.get(this._BATCH_NAME_INPUT_ID).value;
    }

    get dateTimeCreatedLower(): string | null
    {
        return this.accountBatchSearchCriteria.dateTimeCreatedLower ?
            formatLocalYMD(new Date(this.accountBatchSearchCriteria.dateTimeCreatedLower)) :
            null;
    }

    get dateTimeCreatedUpper(): string | null
    {
        let date: Date =
            this.accountBatchSearchCriteria.dateTimeCreatedUpper ?
                new Date(this.accountBatchSearchCriteria.dateTimeCreatedUpper) :
                new Date();

        return formatLocalYMD(date);
    }

    get totalRequestsLower(): string
    {
        return this.form.get(this._TOTAL_REQUESTS_LOWER_INPUT_ID).value;
    }

    get totalRequestsUpper(): string
    {
        return this.form.get(this._TOTAL_REQUESTS_UPPER_INPUT_ID).value;
    }

    get tabActive(): boolean
    {
        return !!this.activeTab;
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            batchNumber: [''],
            batchName: [''],
            createdLower: [''],
            createdUpper: [''],
            totalRequestsLower: [''],
            totalRequestsUpper: ['']
        });

        this.clearPaymentListFilters();
        this.initializeSearchPage();
    }

    ngOnDestroy(): void
    {
        clearInterval(this.interval);
    }

    setSearchCriteria(event: any): void
    {
        let inputId = event.target.id;
        let val = event.target.value;
        this.setAccountBatchSearchCriteria(inputId, val);
    }

    setAccountBatchSearchCriteria(id: string, val: any)
    {
        switch (id)
        {
            case this._BATCH_NUMBER_INPUT_ID:
                this.accountBatchSearchCriteria.batchNumber = <number> val;
                break;
            case this._BATCH_NAME_INPUT_ID:
                this.accountBatchSearchCriteria.batchName = <string> val;
                break;
            case this._TOTAL_REQUESTS_LOWER_INPUT_ID:
                this.accountBatchSearchCriteria.totalRequestsLower = <number> val;
                break;
            case this._TOTAL_REQUESTS_UPPER_INPUT_ID:
                this.accountBatchSearchCriteria.totalRequestsUpper = <number> val;
                break;
            case this._CREATED_LOWER_INPUT_ID:
                this.accountBatchSearchCriteria.dateTimeCreatedLower = toUtcIsoAtLocalStartOfDay(val);
                break;
            case this._CREATED_UPPER_INPUT_ID:
                this.accountBatchSearchCriteria.dateTimeCreatedUpper = toUtcIsoAtLocalEndOfDay(val);
                break;
        }
    }

    getSearchCriteria(): any
    {
        return this.accountBatchSearchCriteria;
    }

    getTabContent(event: any)
    {
        this._activeTab = event.target.id.toString();
        this.saveCurrentFilters();
    }

    clear(activeTab: string): void
    {
        switch (activeTab)
        {
            case this.BATCH_NUMBER_FILTER_ID:
                this.accountBatchSearchCriteria.batchNumber = null;
                this.form.get(this._BATCH_NUMBER_INPUT_ID).reset();
                break;
            case this.BATCH_NAME_FILTER_ID:
                this.accountBatchSearchCriteria.batchName = null;
                this.form.get(this._BATCH_NAME_INPUT_ID).reset();
                break;
            case this.CREATED_FILTER_ID:
                this.accountBatchSearchCriteria.dateTimeCreatedLower = null;
                this.accountBatchSearchCriteria.dateTimeCreatedUpper = null;
                this.clearDateInputField(this.form, this._CREATED_LOWER_INPUT_ID);
                this.clearDateInputField(this.form, this._CREATED_UPPER_INPUT_ID);
                break;
            case this.TOTAL_REQUESTS_FILTER_ID:
                this.accountBatchSearchCriteria.totalRequestsUpper = null;
                this.accountBatchSearchCriteria.totalRequestsLower = null;
                this.form.get(this._TOTAL_REQUESTS_LOWER_INPUT_ID).reset();
                this.form.get(this._TOTAL_REQUESTS_UPPER_INPUT_ID).reset();
                break;
        }
        this.search();
    }

    clearAll(): void
    {
        this.accountBatchSearchCriteria = new AccountBatchSearchCriteriaImpl();
        this.form.reset(this.accountBatchSearchCriteria);
        this.search();
    }

    disableClearAll(): boolean
    {
        return !this.form.dirty;
    }

    disableClear(activeTab: string): boolean
    {
        switch (activeTab)
        {
            case this.BATCH_NUMBER_FILTER_ID:
                return !this.form.get(this._BATCH_NUMBER_INPUT_ID).dirty;
            case this.BATCH_NAME_FILTER_ID:
                return !this.form.get(this._BATCH_NAME_INPUT_ID).dirty;
            case this.TOTAL_REQUESTS_FILTER_ID:
                return !(this.form.get(this._TOTAL_REQUESTS_UPPER_INPUT_ID).dirty || this.form.get(this._TOTAL_REQUESTS_LOWER_INPUT_ID).dirty);
            case this.CREATED_FILTER_ID:
                return !(this.form.get(this._CREATED_UPPER_INPUT_ID).dirty || this.form.get(this._CREATED_LOWER_INPUT_ID).dirty);
            default:
                return true;
        }
    }

    setFilters(): void
    {
        this.applyFilters(
            this.form,
            (criteria: AccountBatchSearchCriteria) => this.accountBatchSearchCriteria = criteria,
            null
        );
    }

    private clearPaymentListFilters()
    {
        const paymentListRouteKey: string = ROUTES.SECURE.ACCOUNT.LIST.path.replace('/secure/', '');
        this.filterPersistenceService.clearFiltersForRoute(paymentListRouteKey);
    }
}